import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import moment from "moment-timezone";

const AUTO_SLIDE_INTERVAL = 5000;
const TIME_CHECK_INTERVAL = 30000;
const SESSION_KEY = "announcement_modal_shown";
const BASE_URL = process.env.REACT_APP_BASEURL;
const TIMEZONE = process.env.REACT_APP_DISPLAY_TIMEZONE;

const isValidAnnouncementDate = (dateStr) => {
    if (!dateStr) return false;
    if (typeof dateStr === "string" && dateStr.trim().startsWith("0000-00-00")) {
        return false;
    }
    const parsed = moment(dateStr);
    return parsed.isValid() && parsed.year() > 1970;
};

const AnnouncementModal = () => {
    const { announcementResult } = useSelector((state) => state.announcement);
    const rawData = Array.isArray(announcementResult) ? announcementResult : [];

    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [progressKey, setProgressKey] = useState(0);
    const [now, setNow] = useState(moment().tz(TIMEZONE));

    useEffect(() => {
        const clockTimer = setInterval(() => {
            setNow(moment().tz(TIMEZONE));
        }, TIME_CHECK_INTERVAL);
        return () => clearInterval(clockTimer);
    }, []);

    const activeAnnouncements = rawData.filter((item) => {
        if (!item) return false;
        if (item.IsActive !== 1) return false;

        const hasValidStart = isValidAnnouncementDate(item.StartDate);
        const hasValidEnd = isValidAnnouncementDate(item.EndDate);

        const start = hasValidStart ? moment.tz(item.StartDate, TIMEZONE) : null;
        const end = hasValidEnd ? moment.tz(item.EndDate, TIMEZONE) : null;

        if (start && now.isBefore(start)) return false;
        if (end && now.isAfter(end)) return false;

        return true;
    });

    useEffect(() => {
        const alreadyShown = sessionStorage.getItem(SESSION_KEY);
        if (!alreadyShown && activeAnnouncements.length > 0) {
            setIsOpen(true);
            sessionStorage.setItem(SESSION_KEY, "true");
        }
    }, [activeAnnouncements.length]);

    useEffect(() => {
        if (isOpen && activeAnnouncements.length === 0) {
            setIsOpen(false);
        }
    }, [activeAnnouncements.length, isOpen]);

    useEffect(() => {
        if (!isOpen || activeAnnouncements.length <= 1) return;
        const timer = setInterval(() => {
            setActiveIndex((prev) => (prev + 1) % activeAnnouncements.length);
            setProgressKey((prev) => prev + 1);
        }, AUTO_SLIDE_INTERVAL);
        return () => clearInterval(timer);
    }, [isOpen, activeAnnouncements.length, activeIndex]);

    useEffect(() => {
        if (activeIndex >= activeAnnouncements.length && activeAnnouncements.length > 0) {
            setActiveIndex(0);
            setProgressKey((prev) => prev + 1);
        }
    }, [activeAnnouncements.length, activeIndex]);

    if (!isOpen || activeAnnouncements.length === 0) return null;

    const goPrev = () => {
        setActiveIndex((prev) =>
            prev === 0 ? activeAnnouncements.length - 1 : prev - 1
        );
        setProgressKey((prev) => prev + 1);
    };

    const goNext = () => {
        setActiveIndex((prev) => (prev + 1) % activeAnnouncements.length);
        setProgressKey((prev) => prev + 1);
    };

    const goTo = (index) => {
        if (index === activeIndex) return;
        setActiveIndex(index);
        setProgressKey((prev) => prev + 1);
    };

    const current = activeAnnouncements[activeIndex];
    if (!current) return null; // extra safety

    return (
        <div className="am-overlay" onClick={() => setIsOpen(false)}>
            <div className="am-card" onClick={(e) => e.stopPropagation()}>
                <div className="am-header">
                    <div className="am-header-title">
                        <span className="am-header-icon">📢</span>
                        <span>Announcement</span>
                    </div>
                    <button className="am-close" onClick={() => setIsOpen(false)} aria-label="Close">
                        &times;
                    </button>
                </div>

                <div className="am-top">
                    <div className="am-image-wrap">
                        {current.AnnouncementLogoURL && (
                            <img
                                src={`${BASE_URL}/announcements/${current.AnnouncementLogoURL}?${Date.now()}`}
                                alt="Announcement"
                                className="am-image"
                            />
                        )}
                    </div>

                    {activeAnnouncements.length > 1 && (
                        <>
                            <button className="am-arrow am-arrow-left" onClick={goPrev} aria-label="Previous">
                                &#10094;
                            </button>
                            <button className="am-arrow am-arrow-right" onClick={goNext} aria-label="Next">
                                &#10095;
                            </button>
                        </>
                    )}
                </div>

                <div className="am-body">
                    {activeAnnouncements.length > 1 && (
                        <div className="am-progress-track">
                            {activeAnnouncements.map((_, index) => (
                                <div
                                    key={`${index}-${progressKey}`}
                                    className="am-progress-bar"
                                    onClick={() => goTo(index)}
                                >
                                    <div
                                        className={`am-progress-fill ${index === activeIndex ? "am-progress-active" : ""
                                            } ${index < activeIndex ? "am-progress-done" : ""}`}
                                        style={{
                                            animationDuration:
                                                index === activeIndex ? `${AUTO_SLIDE_INTERVAL}ms` : "0ms",
                                        }}
                                    ></div>
                                </div>
                            ))}
                        </div>
                    )}

                    <p className="am-description">{current.Description}</p>
                </div>
            </div>
        </div>
    );
};

export default AnnouncementModal;